/**
 * Jobs Charts Section
 *
 * Contains all jobs analytics charts: performance over time,
 * status distribution, jobs by type, completed vs aborted
 */

import type { JobsAnalyticsResponse } from '@skewb-works/contracts';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_COLORS, PIE_COLORS } from '../constants/chartConfig';
import { CustomTooltip, SectionTitle } from './ChartComponents';

interface JobsChartsSectionProps {
  data: JobsAnalyticsResponse;
  statusDistribution: Array<{ name: string; value: number; color: string; id: string }>;
}

export default function JobsChartsSection({ data, statusDistribution }: JobsChartsSectionProps) {
  return (
    <>
      {/* Performance over time */}
      <div className="bg-card border border-border rounded-lg p-5 shadow-sm">
        <SectionTitle>Performance Over Time</SectionTitle>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart
            data={data.performanceOverTime}
            margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient id="gradCompleted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHART_COLORS.green} stopOpacity={0.25} />
                <stop offset="95%" stopColor={CHART_COLORS.green} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradNew" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={CHART_COLORS.blue} stopOpacity={0.2} />
                <stop offset="95%" stopColor={CHART_COLORS.blue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.5} />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
              axisLine={false}
              tickLine={false}
              width={30}
            />
            <CustomTooltip />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            <Area
              type="monotone"
              dataKey="newJobs"
              name="New Jobs"
              stroke={CHART_COLORS.blue}
              fill="url(#gradNew)"
              strokeWidth={2}
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="completed"
              name="Completed"
              stroke={CHART_COLORS.green}
              fill="url(#gradCompleted)"
              strokeWidth={2}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="aborted"
              name="Aborted"
              stroke={CHART_COLORS.red}
              strokeWidth={2}
              strokeDasharray="4 4"
              dot={{ r: 3, fill: CHART_COLORS.red }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Two-column row: status donut + jobs by type bar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-lg p-5 shadow-sm">
          <SectionTitle>Job Status Distribution</SectionTitle>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="50%" height={180}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={48}
                  outerRadius={68}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell
                      key={`cell-status-${entry.id}-${index}`}
                      fill={entry.color}
                      stroke="transparent"
                    />
                  ))}
                </Pie>
                <CustomTooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">
              {statusDistribution.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                      style={{ background: s.color }}
                    />
                    <span className="text-muted-foreground">{s.name}</span>
                  </div>
                  <span className="font-semibold text-foreground tabular-nums">{s.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-5 shadow-sm">
          <SectionTitle>Jobs by Type</SectionTitle>
          {data.jobsByType.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={data.jobsByType}
                layout="vertical"
                margin={{ top: 0, right: 16, bottom: 0, left: 60 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={false}
                  stroke="var(--border)"
                  strokeOpacity={0.5}
                />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="type"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <CustomTooltip />
                <Bar dataKey="count" name="Jobs" radius={[0, 4, 4, 0]}>
                  {data.jobsByType.map((entry, i) => (
                    <Cell key={entry.type} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-200 flex items-center justify-center text-sm text-muted-foreground">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Completed vs Aborted bar */}
      <div className="bg-card border border-border rounded-lg p-5 shadow-sm">
        <SectionTitle>Completed vs Aborted Jobs — Monthly</SectionTitle>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart
            data={data.completedVsAbortedByPeriod}
            margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
            barGap={4}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" strokeOpacity={0.5} />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
              axisLine={false}
              tickLine={false}
              width={30}
            />
            <CustomTooltip />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            <Bar
              dataKey="completed"
              name="Completed"
              fill={CHART_COLORS.green}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
            <Bar
              dataKey="aborted"
              name="Aborted"
              fill={CHART_COLORS.red}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </>
  );
}
